/**
 * Version Check Utility
 * Detects version mismatches and clears stale data
 */

const CURRENT_VERSION = process.env.REACT_APP_VERSION || Date.now().toString();
const VERSION_KEY = 'app_version';
const LAST_CHECK_KEY = 'last_version_check';

/**
 * Check if app version has changed and handle migration
 */
export const checkVersion = () => {
  const storedVersion = localStorage.getItem(VERSION_KEY);
  const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
  const now = Date.now();

  console.log('📦 Current Version:', CURRENT_VERSION);
  console.log('💾 Stored Version:', storedVersion);

  // If version changed, clear stale data
  if (storedVersion && storedVersion !== CURRENT_VERSION) {
    console.log('🔄 Version changed, clearing stale data...');
    handleVersionChange(storedVersion, CURRENT_VERSION);
  }

  // Update stored version
  localStorage.setItem(VERSION_KEY, CURRENT_VERSION);
  localStorage.setItem(LAST_CHECK_KEY, now.toString());

  return {
    current: CURRENT_VERSION,
    previous: storedVersion,
    changed: storedVersion && storedVersion !== CURRENT_VERSION
  };
};

/**
 * Handle version change - clear stale data
 */
function handleVersionChange(oldVersion, newVersion) {
  console.log(`📦 Migrating from ${oldVersion} to ${newVersion}`);

  // Keys to preserve during version change
  const preserveKeys = [
    'language',
    'token',
    'refresh_token',
    'user',
    VERSION_KEY,
    LAST_CHECK_KEY
  ];

  // Clear all localStorage except preserved keys
  const itemsToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && !preserveKeys.includes(key)) {
      itemsToRemove.push(key);
    }
  }

  itemsToRemove.forEach(key => {
    console.log('🗑️ Removing stale key:', key);
    localStorage.removeItem(key);
  });

  // Clear sessionStorage (all temporary data)
  sessionStorage.clear();
  console.log('✅ Stale data cleared');

  // Show notification to user
  showVersionUpdateNotification(oldVersion, newVersion);
}

/**
 * Show version update notification
 */
function showVersionUpdateNotification(oldVersion, newVersion) {
  console.log('✨ New version loaded successfully');
  
  // You can integrate with your toast system here
  if (window.toast) {
    window.toast.success('✨ App updated to latest version!');
  }
}

/**
 * Get current app version
 */
export const getCurrentVersion = () => {
  return CURRENT_VERSION;
};

/**
 * Force version check and reload if needed
 */
export const forceVersionCheck = async () => {
  console.log('🔍 Force checking version...');
  
  // Check service worker version
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.update();
      console.log('✅ Service worker update check complete');
    } catch (error) {
      console.warn('⚠️ Service worker update failed:', error);
    }
  }

  // Check localStorage version
  const versionInfo = checkVersion();
  
  if (versionInfo.changed) {
    console.log('🔄 Version changed, reloading...');
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }

  return versionInfo;
};

/**
 * Clear all app data and reload (nuclear option)
 */
export const resetApp = async () => {
  console.log('💣 Resetting app to clean state...');

  // Clear all storage
  localStorage.clear();
  sessionStorage.clear();

  // Clear all caches
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames.map(cacheName => caches.delete(cacheName))
    );
  }

  // Unregister service workers
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.map(registration => registration.unregister())
    );
  }

  console.log('✅ App reset complete, reloading...');
  window.location.reload(true);
};

export default {
  checkVersion,
  getCurrentVersion,
  forceVersionCheck,
  resetApp
};
